const DEFAULT_TTL_MS = 90_000;
const cache = new Map();

const isDev = process.env.NODE_ENV === 'development';

export const LMS_CACHE_KEYS = {
  adminWorkspace: 'admin:workspace',
  instructorBase: 'instructor:base',
  studentWorkspace: (userId) => `student:workspace:${userId}`,
  studentProfile: (userId) => `student:profile:${userId}`,
};

export const invalidateLmsCache = (prefix = '') => {
  if (!prefix) {
    cache.clear();
    return;
  }

  for (const key of cache.keys()) {
    if (key === prefix || key.startsWith(`${prefix}:`) || key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
};

export const invalidateStudentCache = (userId) => {
  if (!userId) return;
  invalidateLmsCache(LMS_CACHE_KEYS.studentWorkspace(userId));
  invalidateLmsCache(LMS_CACHE_KEYS.studentProfile(userId));
};

export const invalidateAdminCache = () => {
  invalidateLmsCache(LMS_CACHE_KEYS.adminWorkspace);
  invalidateLmsCache(LMS_CACHE_KEYS.instructorBase);
};

export async function fetchAdminWorkspace(options = {}) {
  const { force = false, ttl } = options;
  const { loadAdminWorkspaceRaw } = await import('./lmsDataLoaders');
  return fetchCached(LMS_CACHE_KEYS.adminWorkspace, loadAdminWorkspaceRaw, {
    force,
    ttl,
  });
}

export async function fetchCached(cacheKey, loader, options = {}) {
  if (!cacheKey) {
    return loader();
  }

  const { ttl = DEFAULT_TTL_MS, force = false } = options;
  const now = Date.now();
  const entry = cache.get(cacheKey);

  if (!force && entry?.data !== undefined && entry.expiresAt > now) {
    return entry.data;
  }

  if (entry?.promise) {
    return entry.promise;
  }

  const startedAt = isDev ? performance.now() : 0;

  const promise = Promise.resolve()
    .then(loader)
    .then((data) => {
      cache.set(cacheKey, {
        data,
        expiresAt: Date.now() + ttl,
        promise: null,
      });

      if (isDev) {
        const elapsed = Math.round(performance.now() - startedAt);
        // eslint-disable-next-line no-console
        console.info(`[lms-cache] loaded ${cacheKey} in ${elapsed}ms`);
      }

      return data;
    })
    .catch((error) => {
      const current = cache.get(cacheKey);
      if (current?.promise === promise) {
        cache.delete(cacheKey);
      }
      throw error;
    });

  cache.set(cacheKey, {
    data: entry?.data,
    expiresAt: entry?.expiresAt || 0,
    promise,
  });

  return promise;
}
